// GTJ COMPACT MEMBERSHIP V3
import React, { useCallback, useEffect, useState } from "react";
import { API_BASE_URL } from "./config";
import "./GlobalJobsMembership.css";

const CHECKOUT_ENABLED =
  process.env.REACT_APP_GTJ_CHECKOUT_ENABLED === "true";

let checkoutScriptPromise = null;

function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve();

  if (!checkoutScriptPromise) {
    checkoutScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;

      script.onload = () => {
        if (window.Razorpay) resolve();
        else reject(new Error("Razorpay Checkout unavailable."));
      };

      script.onerror = () =>
        reject(new Error("Unable to load Razorpay Checkout."));

      document.body.appendChild(script);
    }).catch((error) => {
      checkoutScriptPromise = null;
      throw error;
    });
  }

  return checkoutScriptPromise;
}

async function membershipApi(token, path, payload = null) {
  const response = await fetch(
    `${API_BASE_URL}/global-tech-jobs/membership/${path}`,
    {
      method: payload === null ? "GET" : "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(payload !== null
          ? { "Content-Type": "application/json" }
          : {}),
      },
      ...(payload !== null
        ? { body: JSON.stringify(payload) }
        : {}),
    }
  );

  let result = {};

  try {
    result = await response.json();
  } catch {
    // Non-JSON response is handled below.
  }

  if (!response.ok) {
    throw new Error(
      typeof result.detail === "string"
        ? result.detail
        : `Membership request failed (${response.status}).`
    );
  }

  return result;
}

function formatExpiry(value) {
  if (!value) return "Not available";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Not available";

  // The backend expiry is exclusive: 1 January, 00:00 IST.
  // Display the last day of access in India.
  return new Date(date.getTime() - 1000).toLocaleDateString(
    "en-IN",
    {
      timeZone: "Asia/Kolkata",
      day: "numeric",
      month: "long",
      year: "numeric",
    }
  );
}

export default function GlobalJobsMembership({ token, candidate }) {
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const refresh = useCallback(async () => {
    if (!token) return;

    setLoading(true);

    try {
      const data = await membershipApi(token, "status");
      setMembership(data);
      setError("");
    } catch (err) {
      setError(err.message);
      setMembership(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refresh membership when the application wizard completes.
  useEffect(() => {
    const update = () => refresh();

    window.addEventListener(
      "gtj-membership-updated",
      update
    );

    return () => window.removeEventListener(
      "gtj-membership-updated",
      update
    );
  }, [refresh]);


  // GLOBAL TECH JOBS PAYMENT RECOVERY UI V1
  const checkAndRecoverMembership = async () => {
    if (!token || paying || loading) return;

    setLoading(true);
    setError("");
    setNotice("");

    try {
      const status = await membershipApi(token, "status");

      setMembership(status);

      if (status.active) {
        setNotice("Your annual membership is already active.");
        return;
      }

      if (!CHECKOUT_ENABLED) {
        setNotice(
          "Payment verification is not enabled yet. " +
          "Your membership status has been refreshed."
        );
        return;
      }

      // Recovery never creates a new payment order.
      const result = await membershipApi(
        token,
        "reconcile",
        {}
      );

      if (result.membership?.active) {
        setMembership(result.membership);
        setNotice(
          "Your previous payment was verified. " +
          "Annual membership is now active."
        );
      } else if (result.payment_pending) {
        setNotice(
          "Payment confirmation is pending. " +
          "If money was deducted, do not pay again. " +
          "Contact support with your payment reference."
        );
      } else {
        setNotice(
          result.message ||
          "No completed payment was found."
        );
      }
    } catch (err) {
      setError(
        (err.message || "Payment recovery failed.") +
        " If money was deducted, do not pay again " +
        "until your payment is confirmed."
      );
    } finally {
      setLoading(false);
    }
  };

  const beginPayment = async () => {
    if (!CHECKOUT_ENABLED || paying || membership?.active) return;

    setPaying(true);
    setError("");
    setNotice("");

    try {
      // Load checkout before creating the payment order.
      await loadRazorpay();

      const order = await membershipApi(
        token,
        "create-order",
        {}
      );

      if (order.already_paid) {
        setMembership(order.membership);
        setNotice("Your annual membership is already active.");
        return;
      }

      if (
        !order.order_id ||
        !order.key_id ||
        Number(order.amount) !== 4900 ||
        order.currency !== "INR"
      ) {
        throw new Error("Invalid payment order details.");
      }

      const checkout = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "StudyAIPlacement",
        description: `Global Tech Jobs Annual Access - ${order.membership_year}`,
        prefill: {
          email: candidate?.email || "",
          name: candidate?.full_name || "",
        },
        theme: {
          color: "#0d766e",
        },
        handler: async (payment) => {
          setPaying(true);
          setError("");
          setNotice("Verifying your payment securely...");

          try {
            const result = await membershipApi(
              token,
              "verify",
              {
                razorpay_order_id: payment.razorpay_order_id,
                razorpay_payment_id: payment.razorpay_payment_id,
                razorpay_signature: payment.razorpay_signature,
              }
            );

            if (!result.membership?.active) {
              throw new Error(
                "Payment verification requires further confirmation."
              );
            }

            setMembership(result.membership);
            setNotice(
              "Your annual Global Tech Jobs membership is active."
            );
          } catch (err) {
            setError(
              err.message +
              " If money was deducted, do not pay again. " +
              "Contact support before attempting another payment."
            );
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            setNotice(
              "Checkout closed. Your membership status will refresh automatically."
            );
          },
        },
      });

      checkout.on("payment.failed", () => {
        setError(
          "Payment was not completed. If any amount was deducted, " +
          "check your membership status before trying again."
        );
        setPaying(false);
      });

      checkout.open();
    } catch (err) {
      setError(err.message || "Unable to start payment.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <section className="gtjmship">
      <div className="gtjmship-heading">
        <div>
          <span>GLOBAL TECH JOBS ACCESS</span>
          <h3>My Annual Membership</h3>
        </div>
        
      </div>

      {loading ? (
        <p>Checking your annual membership...</p>
      ) : membership?.active ? (
        <div className="gtjmship-active">
          <strong>Annual Membership Active</strong>
          <p>
            You have paid for {membership.year}.
            Your access is valid through{" "}
            <b>{formatExpiry(membership.valid_until)}</b>.
          </p>
          <p>
            No additional annual platform fee is required for
            other eligible job applications during this period.
          </p>
        </div>
      ) : (
        <div className="gtjmship-payment">
          <div className="gtjmship-price">
            <div>
              <span>CALENDAR-YEAR ACCESS</span>
              <h4>Annual Job Platform Membership</h4>
              <p>
                Valid from payment confirmation until
                31 December of the payment year.
              </p>
            </div>
            <strong>₹49</strong>
          </div>

          
          <div className="gtjmship-explanation">
            <strong>What's included</strong>
            <p>
              Apply for jobs, track your applications and access
              AI-assisted resume screening.
            </p>
          </div>

          <button
            type="button"
            className="gtjmship-pay"
            disabled={!CHECKOUT_ENABLED || paying || loading}
            onClick={beginPayment}
          >
            {!CHECKOUT_ENABLED
              ? "Payment Opening Soon"
              : paying
                ? "Processing..."
                : "Buy Annual Membership — ₹49"}
          </button>

          {!CHECKOUT_ENABLED && (
            <p className="gtjmship-small">
              Online payment is not enabled yet.
              No charges can be initiated from this screen.
            </p>
          )}
        </div>
      )}

      {error && (
        <p className="gtjmship-error" role="alert">{error}</p>
      )}

      {notice && (
        <p className="gtjmship-notice" role="status">{notice}</p>
      )}
    </section>
  );
}
